import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import type {
    DataGridPremiumProps,
    GridInitialState,
} from '@mui/x-data-grid-premium';

const STORAGE_PREFIX = 'cat-datagrid-layout:';

const DIMENSION_KEYS = ['width', 'flex', 'minWidth', 'maxWidth'] as const;

type ColumnDimensions = NonNullable<NonNullable<GridInitialState['columns']>['dimensions']>[string];

/** Column layout a user can change by dragging, reordering, hiding, or pinning. */
export type PersistedDataGridLayout = {
    dimensions?: Record<string, ColumnDimensions>;
    orderedFields?: string[];
    columnVisibilityModel?: Record<string, boolean>;
    pinnedColumns?: GridInitialState['pinnedColumns'];
};

type ColumnLike = {
    field: string;
    width?: number;
    flex?: number;
};

export type UsePersistedDataGridLayoutOptions = {
    /** Stable id. When omitted, the key is the sorted column fields. */
    layoutKey?: string;
    columns: readonly ColumnLike[];
    initialState?: GridInitialState;
    /** Tables that already store layout themselves pass false. */
    persistLayout: boolean;
    onColumnWidthChange?: DataGridPremiumProps['onColumnWidthChange'];
    onColumnOrderChange?: DataGridPremiumProps['onColumnOrderChange'];
    onColumnVisibilityModelChange?: DataGridPremiumProps['onColumnVisibilityModelChange'];
    onPinnedColumnsChange?: DataGridPremiumProps['onPinnedColumnsChange'];
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null && !Array.isArray(value);

const stringList = (value: unknown): string[] | undefined => {
    if (!Array.isArray(value)) return undefined;
    const fields = value.filter((item): item is string => typeof item === 'string');
    return fields.length === value.length ? fields : undefined;
};

const readDimensions = (value: unknown): PersistedDataGridLayout['dimensions'] | undefined => {
    if (!isRecord(value)) return undefined;
    const dimensions: NonNullable<PersistedDataGridLayout['dimensions']> = {};
    for (const [field, raw] of Object.entries(value)) {
        if (!isRecord(raw)) continue;
        const dimension: ColumnDimensions = {};
        for (const key of DIMENSION_KEYS) {
            const n = raw[key];
            if (typeof n === 'number' && Number.isFinite(n)) {
                dimension[key] = n;
            }
        }
        if (Object.keys(dimension).length > 0) {
            dimensions[field] = dimension;
        }
    }
    return Object.keys(dimensions).length > 0 ? dimensions : undefined;
};

const readVisibility = (value: unknown): PersistedDataGridLayout['columnVisibilityModel'] | undefined => {
    if (!isRecord(value)) return undefined;
    const model: Record<string, boolean> = {};
    for (const [field, visible] of Object.entries(value)) {
        if (typeof visible === 'boolean') model[field] = visible;
    }
    return model;
};

const readPinned = (value: unknown): PersistedDataGridLayout['pinnedColumns'] | undefined => {
    if (!isRecord(value)) return undefined;
    const left = stringList(value.left);
    const right = stringList(value.right);
    if (!left && !right) return undefined;
    return {left, right};
};

/**
 * Key for one grid. An explicit `layoutKey` stays valid when columns are added;
 * otherwise the sorted field list keeps two grids with the same columns on one layout.
 */
export const persistedDataGridLayoutKey = (
    layoutKey: string | undefined,
    columns: readonly ColumnLike[],
): string | undefined => {
    if (layoutKey) return `${STORAGE_PREFIX}${layoutKey}`;
    if (columns.length === 0) return undefined;
    const fields = columns.map(column => column.field).slice().sort().join('\u0001');
    return `${STORAGE_PREFIX}${fields}`;
};

const browserStorage = (): Storage | undefined => {
    if (typeof window === 'undefined') return undefined;
    try {
        return window.localStorage;
    } catch {
        return undefined;
    }
};

export const readPersistedDataGridLayout = (key: string): PersistedDataGridLayout | undefined => {
    const storage = browserStorage();
    if (!storage) return undefined;
    try {
        const raw = storage.getItem(key);
        if (!raw) return undefined;
        const parsed: unknown = JSON.parse(raw);
        if (!isRecord(parsed)) return undefined;
        const layout: PersistedDataGridLayout = {
            dimensions: readDimensions(parsed.dimensions),
            orderedFields: stringList(parsed.orderedFields),
            columnVisibilityModel: readVisibility(parsed.columnVisibilityModel),
            pinnedColumns: readPinned(parsed.pinnedColumns),
        };
        const hasLayout = layout.dimensions
            || layout.orderedFields
            || layout.columnVisibilityModel
            || layout.pinnedColumns;
        return hasLayout ? layout : undefined;
    } catch {
        return undefined;
    }
};

export const writePersistedDataGridLayout = (key: string, layout: PersistedDataGridLayout): void => {
    const storage = browserStorage();
    if (!storage) return;
    try {
        storage.setItem(key, JSON.stringify(layout));
    } catch {
        // Private mode or a full quota — the grid keeps working for this session.
    }
};

const updatePersistedDataGridLayout = (key: string, patch: PersistedDataGridLayout): void => {
    const current = readPersistedDataGridLayout(key) ?? {};
    writePersistedDataGridLayout(key, {...current, ...patch});
};

/**
 * A stored width replaces flex, so the column stays put while unsaved flex columns
 * still share the remaining space.
 */
export const applySavedColumnWidths = <T extends ColumnLike>(
    columns: readonly T[],
    dimensions: PersistedDataGridLayout['dimensions'],
): readonly T[] => {
    if (!dimensions) return columns;
    let changed = false;
    const next = columns.map(column => {
        const width = dimensions[column.field]?.width;
        if (typeof width !== 'number') return column;
        changed = true;
        return {...column, width, flex: 0};
    });
    return changed ? next : columns;
};

/** Autosize measures only columns the user has not given a width. */
export const autosizeFieldsWithoutSavedWidth = (
    fields: readonly string[],
    dimensions: PersistedDataGridLayout['dimensions'],
    requested?: readonly string[],
): string[] => {
    const saved = new Set(
        Object.entries(dimensions ?? {})
            .filter(([, dimension]) => typeof dimension.width === 'number')
            .map(([field]) => field),
    );
    return (requested ?? fields).filter(field => !saved.has(field));
};

/** Saved layout wins over the caller's `initialState` for the pieces the user has changed. */
export const mergePersistedDataGridLayout = (
    initialState: GridInitialState | undefined,
    layout: PersistedDataGridLayout | undefined,
): GridInitialState | undefined => {
    if (!layout) return initialState;
    const dimensions = layout.dimensions;
    const hasColumns = dimensions || layout.orderedFields || layout.columnVisibilityModel !== undefined;
    if (!hasColumns && !layout.pinnedColumns) return initialState;

    return {
        ...initialState,
        ...(layout.pinnedColumns ? {pinnedColumns: layout.pinnedColumns} : {}),
        columns: {
            ...initialState?.columns,
            ...(layout.orderedFields ? {orderedFields: layout.orderedFields} : {}),
            ...(layout.columnVisibilityModel !== undefined
                ? {columnVisibilityModel: layout.columnVisibilityModel}
                : {}),
            ...(dimensions ? {
                dimensions: {
                    ...initialState?.columns?.dimensions,
                    ...dimensions,
                },
            } : {}),
        },
    };
};

/**
 * Restores column width, order, visibility, and pinning from localStorage, and writes them back
 * when the user changes them. Pagination, filters, and sorting stay with the caller.
 * Autosize widths are not stored — only a drag on the column separator is.
 */
export const usePersistedDataGridLayout = ({
                                               layoutKey,
                                               columns,
                                               initialState,
                                               persistLayout,
                                               onColumnWidthChange,
                                               onColumnOrderChange,
                                               onColumnVisibilityModelChange,
                                               onPinnedColumnsChange,
                                           }: UsePersistedDataGridLayoutOptions) => {
    const storageKey = persistLayout ? persistedDataGridLayoutKey(layoutKey, columns) : undefined;
    const stored = useMemo(
        () => (storageKey ? readPersistedDataGridLayout(storageKey) : undefined),
        [storageKey],
    );
    const [savedDimensions, setSavedDimensions] = useState(stored?.dimensions);
    const [dimensionsKey, setDimensionsKey] = useState(storageKey);
    if (dimensionsKey !== storageKey) {
        setDimensionsKey(storageKey);
        setSavedDimensions(stored?.dimensions);
    }
    // Set while the user is dragging a separator. Autosize publishes the same width event without this.
    const userResizeFieldRef = useRef<string | null>(null);
    // Ignore events published while the grid is hydrating, so defaults are not frozen into storage.
    const canPersistRef = useRef(false);
    useEffect(() => {
        canPersistRef.current = true;
    }, []);

    const noteUserResize = useCallback((field: string | null) => {
        userResizeFieldRef.current = field;
    }, []);

    const save = (patch: PersistedDataGridLayout) => {
        if (!canPersistRef.current || !storageKey) return;
        updatePersistedDataGridLayout(storageKey, patch);
    };

    const handleColumnWidthChange: DataGridPremiumProps['onColumnWidthChange'] = (params, event, details) => {
        onColumnWidthChange?.(params, event, details);
        if (userResizeFieldRef.current !== params.colDef.field || !Number.isFinite(params.width)) return;
        const dimensions = {
            ...savedDimensions,
            [params.colDef.field]: {width: params.width, flex: 0},
        };
        setSavedDimensions(dimensions);
        save({dimensions});
    };

    const handleColumnOrderChange: DataGridPremiumProps['onColumnOrderChange'] = (params, event, details) => {
        onColumnOrderChange?.(params, event, details);
        save({orderedFields: details.api.getAllColumns().map(column => column.field)});
    };

    const handleColumnVisibilityModelChange: DataGridPremiumProps['onColumnVisibilityModelChange'] = (model, details) => {
        onColumnVisibilityModelChange?.(model, details);
        save({columnVisibilityModel: model});
    };

    const handlePinnedColumnsChange: DataGridPremiumProps['onPinnedColumnsChange'] = (pinnedColumns, details) => {
        onPinnedColumnsChange?.(pinnedColumns, details);
        save({pinnedColumns});
    };

    const dimensions = persistLayout ? savedDimensions : undefined;

    return {
        savedDimensions: dimensions,
        columns: applySavedColumnWidths(columns, dimensions),
        noteUserResize,
        initialState: mergePersistedDataGridLayout(initialState, persistLayout ? stored : undefined),
        onColumnWidthChange: handleColumnWidthChange,
        onColumnOrderChange: handleColumnOrderChange,
        onColumnVisibilityModelChange: handleColumnVisibilityModelChange,
        onPinnedColumnsChange: handlePinnedColumnsChange,
    };
};
